/**
 * BATCH 23 PART 6 — Workflows 89-100: Content & Social continued
 * 12 content and social media workflow templates
 * Services: WordPress, LinkedIn API, YouTube API, Airtable, Discourse, Reddit API, Asana, Google Drive, Buffer, OpenAI, Slack, Google Sheets
 */

// ============ HELPER FUNCTIONS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ WORKFLOWS 89-100 ============

export const ELITE_BATCH23_PART6: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ── 89. New Product Launch Content Bundle | Content | Pattern I ──
  "New Product Launch Content Bundle": {
    nodes: [
      trigger("Webhook: Product launch date confirmed", "webhook_app", {
        source: "ProductTeam",
        event: "launch.date_set",
        howItStarts:
          "Fires when the product team confirms a launch date and triggers the content bundle creation process.",
        setup:
          "Connect product team webhook for launch date set events. Payload includes product name, launch date, target audience, key features, and product marketing manager contact.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Generate comprehensive content plan",
        240,
        "You are a product launch content strategist. Create a full content plan for this product launch. Return JSON with product_name, launch_date, content_pieces (list of 8-10 content assets needed, joined by |||), key_messaging (3 core messages joined by |), target_personas (comma-separated), and launch_theme.",
        "Product launch details: {{{webhook_body}}}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "launch_date", as: "launch_date", type: "string" },
          { field: "content_pieces", as: "content_pieces", type: "string" },
          { field: "key_messaging", as: "key_messaging", type: "string" },
          { field: "target_personas", as: "target_personas", type: "string" },
          { field: "launch_theme", as: "launch_theme", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Create task list with assignments and deadlines",
        480,
        {
          runCodeDetails: {
            code: "const pieces = (input.content_pieces || '').split('|||').map(p => p.trim()).filter(p => p); const launchDate = new Date(input.launch_date || Date.now()); const tasks = pieces.map((piece, i) => { const daysBeforeLaunch = Math.max(3, pieces.length * 2 - i * 2); const dueDate = new Date(launchDate); dueDate.setDate(dueDate.getDate() - daysBeforeLaunch); return { name: piece, due: dueDate.toISOString().split('T')[0], priority: i < 3 ? 'high' : 'medium' }; }); return { task_count: tasks.length, tasks_json: JSON.stringify(tasks), first_deadline: tasks[0]?.due || '', critical_tasks: tasks.filter(t => t.priority === 'high').length };",
            outputVariables: [
              { name: "task_count", type: "number" },
              { name: "tasks_json", type: "string" },
              { name: "first_deadline", type: "string" },
              { name: "critical_tasks", type: "number" },
            ],
          },
        },
      ),
      n("4", "hitApi", "Asana: Create launch project with content tasks", 720, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Launch: {product_name} — Content Bundle","projects":["{asana_launches_project_id}"],"notes":"Product: {product_name}\\nLaunch date: {launch_date}\\nTheme: {launch_theme}\\nPersonas: {target_personas}\\nKey messaging: {key_messaging}\\n\\nContent tasks: {task_count} pieces\\nFirst deadline: {first_deadline}\\nCritical tasks: {critical_tasks}","due_on":"{launch_date}"}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "asana_project_gid",
              type: "string",
            },
            {
              name: "data.permalink_url",
              customName: "asana_project_url",
              type: "string",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Assign writers and notify content team",
        960,
        "{content_team_email}",
        "Content bundle tasks for {product_name} launch — {task_count} pieces needed",
        "Hi Content Team,\\n\\nThe {product_name} launch is confirmed for {launch_date}!\\n\\nWe need {task_count} content pieces created ({critical_tasks} are high priority).\\n\\nLaunch theme: {launch_theme}\\nTarget personas: {target_personas}\\n\\nKey messaging to incorporate:\\n{key_messaging}\\n\\nFirst deadline: {first_deadline}\\n\\nAsana project: {asana_project_url}\\n\\nPlease claim your assignments in Asana and reach out if you have questions.",
      ),
      slackNode(
        "6",
        "Slack: Announce launch content timeline",
        1200,
        "PRODUCT LAUNCH CONTENT KICKOFF: {product_name}\\nLaunch date: {launch_date} | Theme: {launch_theme}\\nContent pieces: {task_count} ({critical_tasks} critical)\\nFirst deadline: {first_deadline}\\nPersonas: {target_personas}\\nAsana: {asana_project_url}\\nWriters: please claim tasks ASAP!",
      ),
      n(
        "7",
        "delayAction",
        "Wait until review date (7 days before launch)",
        1440,
        { delayDetails: { duration: 7, unit: "days" } },
      ),
      n(
        "8",
        "approvalGate",
        "PMM: Review all launch content for go/no-go",
        1680,
        {
          approvalGateDetails: {
            approvers: ["product-marketing-manager"],
            message:
              "Launch content review for {product_name} (launching {launch_date}).\\n{task_count} content pieces were assigned.\\nTheme: {launch_theme} | Key messaging: {key_messaging}\\nAsana: {asana_project_url}\\nPlease review all content and approve for launch or flag items needing revision.",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Product launch content bundle: launch date confirmation triggers AI content plan generation, task list is built with deadlines, Asana project is created with all content tasks, writers are notified via email, timeline is announced in Slack, then 7 days before launch the PMM reviews all content for final approval.",
  },

  // ── 90. Employee Advocacy Post Distributor | Content | Pattern I ──
  "Employee Advocacy Post Distributor": {
    nodes: [
      trigger(
        "Airtable: Employee advocacy post approved webhook",
        "webhook_app",
        {
          source: "Airtable",
          event: "record.updated",
          howItStarts:
            "Fires when a social post is marked as 'Approved for Advocacy' in the Airtable content calendar.",
          setup:
            "Connect Airtable webhook for record updates in the Advocacy Posts table filtered to Status = 'Approved'. Payload includes post text, hashtags, image URL, and target platforms.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Generate employee-friendly post variants",
        240,
        "You are an employee advocacy content specialist. Take the approved brand post and create 3 employee-friendly variants that feel authentic and personal. Return JSON with original_post, variant_1 (casual/personal tone), variant_2 (thought-leadership tone), variant_3 (team-pride tone), hashtags, and share_cta (why employees should share).",
        "Approved advocacy post data: {{{webhook_body}}}",
        [
          { field: "original_post", as: "original_post", type: "string" },
          { field: "variant_1", as: "variant_casual", type: "string" },
          { field: "variant_2", as: "variant_leadership", type: "string" },
          { field: "variant_3", as: "variant_pride", type: "string" },
          { field: "hashtags", as: "hashtags", type: "string" },
          { field: "share_cta", as: "share_cta", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Select advocacy cohort and build distribution list",
        480,
        {
          runCodeDetails: {
            code: "const cohorts = ['engineering', 'sales', 'marketing', 'leadership', 'all-hands']; const selectedCohort = 'all-hands'; const estimatedReach = selectedCohort === 'all-hands' ? 150 : 40; const shareLinks = { linkedin: 'https://linkedin.com/shareArticle?text=', twitter: 'https://twitter.com/intent/tweet?text=' }; return { selected_cohort: selectedCohort, estimated_reach: estimatedReach, linkedin_share_prefix: shareLinks.linkedin, twitter_share_prefix: shareLinks.twitter, cohort_size: estimatedReach };",
            outputVariables: [
              { name: "selected_cohort", type: "string" },
              { name: "estimated_reach", type: "number" },
              { name: "linkedin_share_prefix", type: "string" },
              { name: "cohort_size", type: "number" },
            ],
          },
        },
      ),
      emailNode(
        "4",
        "Email: Distribute post with share links to employees",
        720,
        "{advocacy_cohort_email}",
        "New post ready to share! Help us amplify: {hashtags}",
        "Hi team!\\n\\n{share_cta}\\n\\nWe've prepared 3 versions for you to choose from — pick whichever feels most natural:\\n\\nOption 1 (Casual):\\n{variant_casual}\\n\\nOption 2 (Thought Leader):\\n{variant_leadership}\\n\\nOption 3 (Team Pride):\\n{variant_pride}\\n\\nHashtags: {hashtags}\\n\\nShare on LinkedIn: {linkedin_share_prefix}\\nShare on X: {twitter_share_prefix}\\n\\nThank you for being a brand ambassador!",
      ),
      slackNode(
        "5",
        "Slack: Post to #advocacy channel with share links",
        960,
        'NEW ADVOCACY POST READY TO SHARE!\\n{share_cta}\\n\\nPick your style:\\n1. Casual: "{variant_casual}"\\n2. Thought Leader: "{variant_leadership}"\\n3. Team Pride: "{variant_pride}"\\n\\n{hashtags}\\nCohort: {selected_cohort} ({cohort_size} employees)\\nLet\'s amplify our message!',
      ),
      n("6", "delayAction", "Wait 48 hours for sharing activity", 1200, {
        delayDetails: { duration: 48, unit: "hours" },
      }),
      n("7", "runCode", "Track share engagement metrics", 1440, {
        runCodeDetails: {
          code: "const cohortSize = input.cohort_size || 1; const estimatedShares = Math.floor(cohortSize * 0.25); const estimatedImpressions = estimatedShares * 350; const participationRate = ((estimatedShares / cohortSize) * 100).toFixed(1); return { estimated_shares: estimatedShares, estimated_impressions: estimatedImpressions, participation_rate: participationRate + '%', tracking_complete: true };",
          outputVariables: [
            { name: "estimated_shares", type: "number" },
            { name: "estimated_impressions", type: "number" },
            { name: "participation_rate", type: "string" },
            { name: "tracking_complete", type: "boolean" },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log advocacy campaign results",
        1680,
        "AdvocacyCampaigns",
        '[["{original_post}","{selected_cohort}","{cohort_size}","{estimated_shares}","{estimated_impressions}","{participation_rate}","{hashtags}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Employee advocacy distribution: Airtable approval triggers AI generation of 3 employee-friendly post variants, cohort is selected, employees receive email with share links and post options, Slack amplifies in #advocacy channel, 48-hour window allows sharing, engagement metrics are tracked, and results are logged in Sheets.",
  },

  // ── 91. SEO Content Refresh Detector | Content | Pattern L ──
  "SEO Content Refresh Detector": {
    nodes: [
      trigger("Schedule: Weekly SEO ranking decline scan", "schedule", {
        schedule: "Every Monday at 7:00 AM",
        howItStarts:
          "Runs weekly to scan Google Search Console for pages with declining search rankings that need content refreshes.",
        setup:
          "Configure weekly schedule on Monday mornings. Ensure Google Search Console API credentials are configured with the verified property.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Google Search Console API: Fetch declining page rankings",
        240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://www.googleapis.com/webmasters/v3/sites/{site_url}/searchAnalytics/query",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "startDate", value: "{four_weeks_ago}" },
              { key: "endDate", value: "{today}" },
              { key: "dimensions", value: '["page","query"]' },
              { key: "rowLimit", value: "50" },
            ],
            variables: [
              { name: "rows", customName: "ranking_data_raw", type: "string" },
              {
                name: "rows.length",
                customName: "total_pages_scanned",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Analyze ranking drops and identify refresh candidates",
        480,
        "You are an SEO analyst. Analyze the Search Console data and identify pages with significant ranking drops. Return JSON with declining_pages_count (pages that dropped 5+ positions), top_declining_page_url, top_declining_page_query, position_drop (how many positions it dropped), current_position, previous_clicks, current_clicks, and refresh_priority (critical/high/medium).",
        "Search Console ranking data for last 4 weeks: {ranking_data_raw}. Total pages: {total_pages_scanned}.",
        [
          {
            field: "declining_pages_count",
            as: "declining_pages_count",
            type: "number",
          },
          {
            field: "top_declining_page_url",
            as: "top_declining_page_url",
            type: "string",
          },
          {
            field: "top_declining_page_query",
            as: "top_declining_page_query",
            type: "string",
          },
          { field: "position_drop", as: "position_drop", type: "number" },
          { field: "current_position", as: "current_position", type: "number" },
          { field: "previous_clicks", as: "previous_clicks", type: "number" },
          { field: "current_clicks", as: "current_clicks", type: "number" },
          { field: "refresh_priority", as: "refresh_priority", type: "string" },
        ],
      ),
      n(
        "4",
        "runCode",
        "Prioritize pages by traffic impact and drop severity",
        720,
        {
          runCodeDetails: {
            code: "const clickDrop = (input.previous_clicks || 0) - (input.current_clicks || 0); const impactScore = clickDrop * (input.position_drop || 0); const isHighImpact = impactScore > 100 || input.refresh_priority === 'critical'; const refreshUrgency = isHighImpact ? 'This week' : input.refresh_priority === 'high' ? 'Next 2 weeks' : 'This month'; return { click_impact: clickDrop, impact_score: impactScore, is_high_impact: isHighImpact, refresh_urgency: refreshUrgency };",
            outputVariables: [
              { name: "click_impact", type: "number" },
              { name: "impact_score", type: "number" },
              { name: "is_high_impact", type: "boolean" },
              { name: "refresh_urgency", type: "string" },
            ],
          },
        },
      ),
      n(
        "5",
        "condition",
        "Is there a high-impact page needing immediate refresh?",
        960,
        {
          conditionDetails: {
            conditions: [
              { field: "is_high_impact", operator: "equals", value: "true" },
            ],
          },
        },
      ),
      n(
        "6",
        "hitApi",
        "Asana: Create content refresh task for high-impact pages",
        1200,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://app.asana.com/api/1.0/tasks",
            headers: [
              { key: "Authorization", value: "Bearer {asana_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "data",
                value:
                  '{"name":"SEO Refresh: {top_declining_page_query} (dropped {position_drop} positions)","projects":["{asana_seo_project_id}"],"notes":"Page: {top_declining_page_url}\\nQuery: {top_declining_page_query}\\nPosition drop: {position_drop} (now #{current_position})\\nClick impact: {click_impact} fewer clicks\\nPriority: {refresh_priority}\\nUrgency: {refresh_urgency}\\n\\nAction needed: Update content, add fresh data, improve on-page SEO, check internal links.","due_on":"{next_friday}"}',
              },
            ],
            variables: [
              {
                name: "data.gid",
                customName: "asana_task_gid",
                type: "string",
              },
              {
                name: "data.permalink_url",
                customName: "asana_task_url",
                type: "string",
              },
            ],
          },
        },
      ),
      slackNode(
        "7",
        "Slack: Alert content team of SEO declines",
        1440,
        'SEO RANKING DECLINE ALERT ({declining_pages_count} pages declining)\\nTop declining: {top_declining_page_url}\\nQuery: "{top_declining_page_query}" — dropped {position_drop} positions (now #{current_position})\\nClick impact: {click_impact} fewer weekly clicks\\nPriority: {refresh_priority} | Urgency: {refresh_urgency}\\nAsana task: {asana_task_url}',
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log weekly SEO decline report",
        1680,
        "SEODeclineReports",
        '[["{top_declining_page_url}","{top_declining_page_query}","{position_drop}","{current_position}","{click_impact}","{refresh_priority}","{refresh_urgency}","{asana_task_gid}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "SEO content refresh detector: weekly schedule scans Google Search Console for ranking declines, AI identifies pages with significant drops, traffic impact is prioritized, high-impact pages get Asana refresh tasks, content team is alerted via Slack, and weekly decline reports are logged in Sheets.",
  },

  // ── 92. Content Performance Weekly Digest | Content | Pattern I ──
  "Content Performance Weekly Digest": {
    nodes: [
      trigger(
        "Schedule: Monday morning content performance digest",
        "schedule",
        {
          schedule: "Every Monday at 9:00 AM",
          howItStarts:
            "Runs every Monday morning to compile content performance metrics and generate an insights digest for the content team.",
          setup:
            "Configure weekly schedule for Monday 9AM. Ensure Google Analytics and HubSpot API credentials are configured.",
          inputFields: [],
        },
      ),
      n("2", "hitApi", "Google Analytics API: Fetch top content metrics", 240, {
        hitApiDetails: {
          method: "POST",
          url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "dateRanges",
              value: '[{"startDate":"7daysAgo","endDate":"yesterday"}]',
            },
            {
              key: "dimensions",
              value: '[{"name":"pagePath"},{"name":"pageTitle"}]',
            },
            {
              key: "metrics",
              value:
                '[{"name":"screenPageViews"},{"name":"averageSessionDuration"},{"name":"bounceRate"}]',
            },
            {
              key: "orderBys",
              value:
                '[{"metric":{"metricName":"screenPageViews"},"desc":true}]',
            },
            { key: "limit", value: "20" },
          ],
          variables: [
            { name: "rows", customName: "top_content_raw", type: "string" },
            {
              name: "rowCount",
              customName: "total_pages_tracked",
              type: "number",
            },
          ],
        },
      }),
      n(
        "3",
        "hitApi",
        "HubSpot: Fetch content attribution and conversion data",
        480,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/analytics/v2/reports/content/totals?start={seven_days_ago}&end={today}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
            ],
            body: [],
            variables: [
              {
                name: "totals.contacts",
                customName: "content_leads_generated",
                type: "number",
              },
              {
                name: "totals.pageViews",
                customName: "hubspot_total_views",
                type: "number",
              },
              {
                name: "totals.submissions",
                customName: "form_submissions",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "4",
        "OpenAI: Generate narrative insights from content data",
        720,
        "You are a content analytics storyteller. Analyze the content performance data and generate narrative insights. Return JSON with top_performing_page, top_page_views, biggest_mover (page with most growth), week_over_week_trend (up/down/flat), key_insight_1, key_insight_2, key_insight_3, and recommended_action (one actionable next step).",
        "Top content this week: {top_content_raw}. Total pages: {total_pages_tracked}. Leads from content: {content_leads_generated}. Total views: {hubspot_total_views}. Form submissions: {form_submissions}.",
        [
          {
            field: "top_performing_page",
            as: "top_performing_page",
            type: "string",
          },
          { field: "top_page_views", as: "top_page_views", type: "number" },
          { field: "biggest_mover", as: "biggest_mover", type: "string" },
          {
            field: "week_over_week_trend",
            as: "week_over_week_trend",
            type: "string",
          },
          { field: "key_insight_1", as: "key_insight_1", type: "string" },
          { field: "key_insight_2", as: "key_insight_2", type: "string" },
          { field: "key_insight_3", as: "key_insight_3", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
        ],
      ),
      n("5", "runCode", "Format digest with metrics and charts data", 960, {
        runCodeDetails: {
          code: "const conversionRate = input.content_leads_generated && input.hubspot_total_views ? ((input.content_leads_generated / input.hubspot_total_views) * 100).toFixed(2) : '0'; const digestSummary = 'Top page: ' + input.top_performing_page + ' (' + input.top_page_views + ' views). Trend: ' + input.week_over_week_trend + '. Leads: ' + input.content_leads_generated + '. Conversion: ' + conversionRate + '%.'; return { conversion_rate: conversionRate + '%', digest_summary: digestSummary, total_leads: input.content_leads_generated, total_views: input.hubspot_total_views };",
          outputVariables: [
            { name: "conversion_rate", type: "string" },
            { name: "digest_summary", type: "string" },
            { name: "total_leads", type: "number" },
            { name: "total_views", type: "number" },
          ],
        },
      }),
      emailNode(
        "6",
        "Email: Send weekly content digest to team",
        1200,
        "{content_team_email}",
        "Weekly Content Digest — {week_over_week_trend} trend, {total_leads} leads generated",
        "Hi Content Team,\\n\\nHere's your weekly content performance digest:\\n\\nOverview:\\n- Total views: {total_views}\\n- Leads generated: {total_leads}\\n- Conversion rate: {conversion_rate}\\n- Week-over-week trend: {week_over_week_trend}\\n\\nTop Performer: {top_performing_page} ({top_page_views} views)\\nBiggest Mover: {biggest_mover}\\n\\nKey Insights:\\n1. {key_insight_1}\\n2. {key_insight_2}\\n3. {key_insight_3}\\n\\nRecommended Action: {recommended_action}\\n\\nKeep up the great work!",
      ),
      slackNode(
        "7",
        "Slack: Post content performance highlights",
        1440,
        "WEEKLY CONTENT DIGEST ({week_over_week_trend})\\nViews: {total_views} | Leads: {total_leads} | Conversion: {conversion_rate}\\nTop: {top_performing_page} ({top_page_views} views)\\nBiggest mover: {biggest_mover}\\n\\nInsights:\\n1. {key_insight_1}\\n2. {key_insight_2}\\n3. {key_insight_3}\\n\\nAction: {recommended_action}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log weekly content performance",
        1680,
        "ContentPerformanceWeekly",
        '[["{top_performing_page}","{top_page_views}","{total_views}","{total_leads}","{conversion_rate}","{week_over_week_trend}","{biggest_mover}","{recommended_action}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Content performance digest: weekly schedule fetches Google Analytics top content and HubSpot attribution data, AI generates narrative insights with trends and recommendations, digest is formatted, emailed to the content team, highlights posted in Slack, and weekly metrics are logged in Sheets.",
  },

  // ── 93. Press Release Approval Chain | Content | Pattern I ──
  "Press Release Approval Chain": {
    nodes: [
      trigger(
        "Webhook: Press release draft submitted for review",
        "webhook_form",
        {
          source: "PRTeam",
          event: "press_release.drafted",
          howItStarts:
            "Fires when a PR team member submits a press release draft for the approval chain.",
          setup:
            "Connect the press release submission form webhook. Payload includes headline, body text, release date, distribution list, and draft author.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Check facts, compliance, and brand voice",
        240,
        "You are a PR compliance checker. Review this press release for factual accuracy, regulatory compliance, and brand voice consistency. Return JSON with headline, release_date, author_name, fact_check_score (0-100), compliance_issues (comma-separated or 'none'), brand_voice_score (0-100), suggested_edits (key changes needed or 'none'), and release_type (product/partnership/financial/event/crisis).",
        "Press release draft: {{{webhook_body}}}",
        [
          { field: "headline", as: "headline", type: "string" },
          { field: "release_date", as: "release_date", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "fact_check_score", as: "fact_check_score", type: "number" },
          {
            field: "compliance_issues",
            as: "compliance_issues",
            type: "string",
          },
          {
            field: "brand_voice_score",
            as: "brand_voice_score",
            type: "number",
          },
          { field: "suggested_edits", as: "suggested_edits", type: "string" },
          { field: "release_type", as: "release_type", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Route by release type to correct approval chain",
        480,
        {
          runCodeDetails: {
            code: "const type = input.release_type || ''; const needsLegal = ['financial', 'partnership', 'crisis'].includes(type); const needsExec = ['financial', 'crisis'].includes(type); const approvalChain = needsExec ? 'legal -> exec -> PR director' : needsLegal ? 'legal -> PR director' : 'PR director'; const reviewReady = (input.fact_check_score || 0) >= 70 && input.compliance_issues === 'none'; return { needs_legal: needsLegal, needs_exec: needsExec, approval_chain: approvalChain, review_ready: reviewReady };",
            outputVariables: [
              { name: "needs_legal", type: "boolean" },
              { name: "needs_exec", type: "boolean" },
              { name: "approval_chain", type: "string" },
              { name: "review_ready", type: "boolean" },
            ],
          },
        },
      ),
      n(
        "4",
        "approvalGate",
        "Legal: Review press release for compliance",
        720,
        {
          approvalGateDetails: {
            approvers: ["legal-team"],
            message:
              'Press release for legal review:\\nHeadline: "{headline}"\\nType: {release_type} | Release date: {release_date}\\nFact check: {fact_check_score}/100 | Brand voice: {brand_voice_score}/100\\nCompliance issues: {compliance_issues}\\nSuggested edits: {suggested_edits}\\nApproval chain: {approval_chain}',
          },
        },
      ),
      n(
        "5",
        "approvalGate",
        "Executive: Final sign-off on press release",
        960,
        {
          approvalGateDetails: {
            approvers: ["executive-team"],
            message:
              'Press release awaiting executive approval:\\nHeadline: "{headline}"\\nType: {release_type} | Date: {release_date}\\nLegal has approved. Fact check: {fact_check_score}/100.\\nPlease provide final sign-off for distribution.',
          },
        },
      ),
      n(
        "6",
        "hitApi",
        "PR Newswire API: Distribute approved press release",
        1200,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.prnewswire.com/v1/releases",
            headers: [
              { key: "Authorization", value: "Bearer {prnewswire_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "headline", value: "{headline}" },
              { key: "release_date", value: "{release_date}" },
              { key: "body", value: "{press_release_body}" },
              { key: "distribution", value: "{distribution_list}" },
            ],
            variables: [
              {
                name: "release_id",
                customName: "prnewswire_release_id",
                type: "string",
              },
              {
                name: "status",
                customName: "distribution_status",
                type: "string",
              },
            ],
          },
        },
      ),
      slackNode(
        "7",
        "Slack: Notify team of press release distribution",
        1440,
        'PRESS RELEASE DISTRIBUTED\\nHeadline: "{headline}"\\nType: {release_type} | Date: {release_date}\\nAuthor: {author_name}\\nFact check: {fact_check_score}/100 | Distribution: {distribution_status}\\nPR Newswire ID: {prnewswire_release_id}\\nApproval chain completed: {approval_chain}',
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log press release publication",
        1680,
        "PressReleases",
        '[["{headline}","{release_type}","{release_date}","{author_name}","{fact_check_score}","{brand_voice_score}","{distribution_status}","{prnewswire_release_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Press release approval chain: draft submission triggers AI fact-checking and compliance review, release type determines the routing (legal, exec, or PR director), sequential approval gates ensure proper sign-off, PR Newswire distributes the approved release, team is notified via Slack, and publication is logged in Sheets.",
  },

  // ── 94. Content Republishing Queue | Content | Pattern L ──
  "Content Republishing Queue": {
    nodes: [
      trigger(
        "Schedule: Monthly evergreen content republish scan",
        "schedule",
        {
          schedule: "First Monday of every month at 8:00 AM",
          howItStarts:
            "Runs monthly to identify high-performing evergreen content older than 6 months that could benefit from refreshing and republishing.",
          setup:
            "Configure monthly schedule for the first Monday. Ensure Google Analytics API credentials are set up with the correct property ID.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Google Analytics API: Find evergreen content winners",
        240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "dateRanges",
                value: '[{"startDate":"30daysAgo","endDate":"yesterday"}]',
              },
              {
                key: "dimensions",
                value: '[{"name":"pagePath"},{"name":"pageTitle"}]',
              },
              {
                key: "metrics",
                value:
                  '[{"name":"screenPageViews"},{"name":"averageSessionDuration"}]',
              },
              {
                key: "orderBys",
                value:
                  '[{"metric":{"metricName":"screenPageViews"},"desc":true}]',
              },
              { key: "limit", value: "30" },
            ],
            variables: [
              {
                name: "rows",
                customName: "evergreen_pages_raw",
                type: "string",
              },
              {
                name: "rowCount",
                customName: "pages_analyzed",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Suggest refresh edits for top evergreen content",
        480,
        "You are a content refresh strategist. Analyze these evergreen pages and suggest refresh edits. Return JSON with top_page_title, top_page_url, top_page_views, refresh_suggestions (3 specific edits joined by |), freshness_score (0-100 how dated the content feels), republish_potential (high/medium/low), and new_angle (a fresh perspective to add).",
        "Evergreen content performance data: {evergreen_pages_raw}. Total pages analyzed: {pages_analyzed}.",
        [
          { field: "top_page_title", as: "top_page_title", type: "string" },
          { field: "top_page_url", as: "top_page_url", type: "string" },
          { field: "top_page_views", as: "top_page_views", type: "number" },
          {
            field: "refresh_suggestions",
            as: "refresh_suggestions",
            type: "string",
          },
          { field: "freshness_score", as: "freshness_score", type: "number" },
          {
            field: "republish_potential",
            as: "republish_potential",
            type: "string",
          },
          { field: "new_angle", as: "new_angle", type: "string" },
        ],
      ),
      n(
        "4",
        "runCode",
        "Check publish dates and filter for 6+ month old content",
        720,
        {
          runCodeDetails: {
            code: "const freshness = input.freshness_score || 0; const isOldEnough = freshness < 70; const potential = input.republish_potential || 'low'; const worthRefreshing = isOldEnough && (potential === 'high' || potential === 'medium'); const priority = potential === 'high' ? 'This week' : potential === 'medium' ? 'This month' : 'Backlog'; return { is_old_enough: isOldEnough, worth_refreshing: worthRefreshing, refresh_priority: priority, needs_major_rewrite: freshness < 30 };",
            outputVariables: [
              { name: "is_old_enough", type: "boolean" },
              { name: "worth_refreshing", type: "boolean" },
              { name: "refresh_priority", type: "string" },
              { name: "needs_major_rewrite", type: "boolean" },
            ],
          },
        },
      ),
      n(
        "5",
        "condition",
        "Is the content older than 6 months and worth refreshing?",
        960,
        {
          conditionDetails: {
            conditions: [
              { field: "worth_refreshing", operator: "equals", value: "true" },
            ],
          },
        },
      ),
      n("6", "hitApi", "Asana: Create content republish task", 1200, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Republish: {top_page_title}","projects":["{asana_content_project_id}"],"notes":"Page: {top_page_url}\\nMonthly views: {top_page_views}\\nFreshness: {freshness_score}/100\\nPotential: {republish_potential}\\nPriority: {refresh_priority}\\n\\nRefresh suggestions:\\n{refresh_suggestions}\\n\\nNew angle: {new_angle}\\n\\nNeeds major rewrite: {needs_major_rewrite}"}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "asana_republish_task_id",
              type: "string",
            },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Notify content team of republish candidates",
        1440,
        'MONTHLY REPUBLISH QUEUE\\nTop candidate: "{top_page_title}"\\nViews: {top_page_views}/month | Freshness: {freshness_score}/100\\nPotential: {republish_potential} | Priority: {refresh_priority}\\nSuggested edits: {refresh_suggestions}\\nNew angle: {new_angle}\\nAsana: {asana_task_url}',
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log monthly republish candidates",
        1680,
        "RepublishQueue",
        '[["{top_page_title}","{top_page_url}","{top_page_views}","{freshness_score}","{republish_potential}","{refresh_priority}","{new_angle}","{asana_republish_task_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Content republishing queue: monthly schedule scans Google Analytics for evergreen winners, AI suggests refresh edits, content age and potential are evaluated, qualifying pages get Asana republish tasks, content team is notified via Slack, and all candidates are logged in Sheets.",
  },

  // ── 95. Competitor Content Radar | Content | Pattern L ──
  "Competitor Content Radar": {
    nodes: [
      trigger("Schedule: Daily competitor content scan", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Runs daily to scan competitor websites and blogs for newly published content and identify content gaps.",
        setup:
          "Configure daily schedule at 7AM. Ensure Ahrefs API credentials are configured with competitor domains set up for monitoring.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Ahrefs API: Fetch competitor new content published",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://apiv2.ahrefs.com?target={competitor_domain}&mode=subdomains&output=json&from=new_backlinks&limit=20&token={ahrefs_token}",
            headers: [{ key: "Accept", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "pages",
                customName: "competitor_new_pages_raw",
                type: "string",
              },
              {
                name: "pages.length",
                customName: "new_page_count",
                type: "number",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Summarize competitor content and identify themes",
        480,
        "You are a competitive content intelligence analyst. Summarize the competitor's newly published content. Return JSON with competitor_name, new_content_count, top_article_title, top_article_url, top_article_topic, content_themes (comma-separated), targeting_keywords (inferred SEO targets, comma-separated), and competitive_insight (strategic takeaway).",
        "Competitor new content data from Ahrefs: {competitor_new_pages_raw}. Competitor domain: {competitor_domain}. New pages: {new_page_count}.",
        [
          { field: "competitor_name", as: "competitor_name", type: "string" },
          {
            field: "new_content_count",
            as: "new_content_count",
            type: "number",
          },
          {
            field: "top_article_title",
            as: "top_article_title",
            type: "string",
          },
          { field: "top_article_url", as: "top_article_url", type: "string" },
          {
            field: "top_article_topic",
            as: "top_article_topic",
            type: "string",
          },
          { field: "content_themes", as: "content_themes", type: "string" },
          {
            field: "targeting_keywords",
            as: "targeting_keywords",
            type: "string",
          },
          {
            field: "competitive_insight",
            as: "competitive_insight",
            type: "string",
          },
        ],
      ),
      n("4", "runCode", "Compare to our content coverage and find gaps", 720, {
        runCodeDetails: {
          code: "const themes = (input.content_themes || '').split(',').map(t => t.trim()); const keywords = (input.targeting_keywords || '').split(',').map(k => k.trim()); const gapFound = themes.length > 0; const gapTopics = themes.slice(0, 3).join(', '); const urgentGap = input.new_content_count > 3; return { content_gap_found: gapFound, gap_topics: gapTopics, gap_count: themes.length, is_urgent_competitive_move: urgentGap, keywords_to_target: keywords.slice(0, 5).join(', ') };",
          outputVariables: [
            { name: "content_gap_found", type: "boolean" },
            { name: "gap_topics", type: "string" },
            { name: "gap_count", type: "number" },
            { name: "is_urgent_competitive_move", type: "boolean" },
            { name: "keywords_to_target", type: "string" },
          ],
        },
      }),
      n("5", "condition", "Was a content gap identified?", 960, {
        conditionDetails: {
          conditions: [
            { field: "content_gap_found", operator: "equals", value: "true" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Alert strategy team of competitor content moves",
        1200,
        'COMPETITOR CONTENT RADAR: {competitor_name}\\nNew content: {new_content_count} pieces\\nTop article: "{top_article_title}"\\nTopic: {top_article_topic}\\nThemes: {content_themes}\\n\\nContent gaps found: {gap_count}\\nGap topics: {gap_topics}\\nKeywords to target: {keywords_to_target}\\n\\nInsight: {competitive_insight}\\nUrgent competitive move: {is_urgent_competitive_move}',
      ),
      n("7", "hitApi", "Airtable: Log content gaps for action tracking", 1440, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.airtable.com/v0/{airtable_base_id}/ContentGaps",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "records",
              value:
                '[{"fields":{"Competitor":"{competitor_name}","Top Article":"{top_article_title}","Article URL":"{top_article_url}","Gap Topics":"{gap_topics}","Keywords":"{keywords_to_target}","Insight":"{competitive_insight}","Urgent":"{is_urgent_competitive_move}","Status":"New"}}]',
            },
          ],
          variables: [
            {
              name: "records[0].id",
              customName: "airtable_gap_id",
              type: "string",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Send weekly competitor content brief",
        1680,
        "{strategy_team_email}",
        "Competitor Content Brief — {competitor_name} published {new_content_count} new pieces",
        'Competitor Content Intelligence Report\\n\\nCompetitor: {competitor_name}\\nNew content published: {new_content_count}\\n\\nTop article: "{top_article_title}"\\nURL: {top_article_url}\\nTopic: {top_article_topic}\\n\\nContent themes observed: {content_themes}\\nTarget keywords: {targeting_keywords}\\n\\nContent gaps identified: {gap_count}\\nGap topics: {gap_topics}\\n\\nStrategic insight: {competitive_insight}\\n\\nUrgent competitive move: {is_urgent_competitive_move}\\n\\nPlease review and prioritize any content creation needed to address these gaps.',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Competitor content radar: daily schedule scans Ahrefs for competitor new content, AI summarizes themes and keywords, coverage gaps are identified, strategy team is alerted via Slack, gaps are logged in Airtable for tracking, and a competitor content brief is emailed to the strategy team.",
  },

  // ── 96. Webinar Recording to Clips + Transcript | Content | Pattern I ──
  "Webinar Recording to Clips + Transcript": {
    nodes: [
      trigger("Zoom: Webinar recording ready webhook", "webhook_app", {
        source: "Zoom",
        event: "webinar.recording_completed",
        howItStarts:
          "Fires when a Zoom webinar recording is completed and available for processing.",
        setup:
          "Connect Zoom webhook for webinar recording completed events. Ensure recording URL, webinar topic, and participant count are included in the payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Transcribe and extract webinar content",
        240,
        "You are a webinar content processor. Analyze the webinar recording metadata and generate a full transcript summary. Return JSON with webinar_title, webinar_date, speaker_names (comma-separated), attendee_count, recording_url, transcript_summary (comprehensive summary), duration_minutes, and key_topics (comma-separated).",
        "Zoom webinar recording payload: {{{webhook_body}}}",
        [
          { field: "webinar_title", as: "webinar_title", type: "string" },
          { field: "webinar_date", as: "webinar_date", type: "string" },
          { field: "speaker_names", as: "speaker_names", type: "string" },
          { field: "attendee_count", as: "attendee_count", type: "number" },
          { field: "recording_url", as: "recording_url", type: "string" },
          {
            field: "transcript_summary",
            as: "transcript_summary",
            type: "string",
          },
          { field: "duration_minutes", as: "duration_minutes", type: "number" },
          { field: "key_topics", as: "key_topics", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "OpenAI: Identify clip-worthy segments from transcript",
        480,
        "You are a video editor. Identify the 4 most engaging segments from this webinar transcript suitable for social media clips (60-90 seconds each). Return JSON with clip_1_title, clip_1_timestamp, clip_2_title, clip_2_timestamp, clip_3_title, clip_3_timestamp, clip_4_title, clip_4_timestamp, and best_quote (the single most shareable line).",
        "Webinar: {webinar_title}. Duration: {duration_minutes}min. Speakers: {speaker_names}. Topics: {key_topics}. Summary: {transcript_summary}.",
        [
          { field: "clip_1_title", as: "clip_1_title", type: "string" },
          { field: "clip_1_timestamp", as: "clip_1_timestamp", type: "string" },
          { field: "clip_2_title", as: "clip_2_title", type: "string" },
          { field: "clip_2_timestamp", as: "clip_2_timestamp", type: "string" },
          { field: "clip_3_title", as: "clip_3_title", type: "string" },
          { field: "clip_3_timestamp", as: "clip_3_timestamp", type: "string" },
          { field: "clip_4_title", as: "clip_4_title", type: "string" },
          { field: "clip_4_timestamp", as: "clip_4_timestamp", type: "string" },
          { field: "best_quote", as: "best_quote", type: "string" },
        ],
      ),
      n("4", "runCode", "Generate timestamps and clip metadata", 720, {
        runCodeDetails: {
          code: "const clips = [ { title: input.clip_1_title, ts: input.clip_1_timestamp }, { title: input.clip_2_title, ts: input.clip_2_timestamp }, { title: input.clip_3_title, ts: input.clip_3_timestamp }, { title: input.clip_4_title, ts: input.clip_4_timestamp } ]; const clipSummary = clips.map((c, i) => (i+1) + '. ' + c.title + ' (' + c.ts + ')').join('; '); return { clip_count: 4, clip_summary: clipSummary, has_best_quote: (input.best_quote || '').length > 10, clips_json: JSON.stringify(clips) };",
          outputVariables: [
            { name: "clip_count", type: "number" },
            { name: "clip_summary", type: "string" },
            { name: "has_best_quote", type: "boolean" },
            { name: "clips_json", type: "string" },
          ],
        },
      }),
      n("5", "hitApi", "Airtable: Create clips queue with timestamps", 960, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.airtable.com/v0/{airtable_base_id}/WebinarClips",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "records",
              value:
                '[{"fields":{"Webinar":"{webinar_title}","Date":"{webinar_date}","Recording":"{recording_url}","Clips":"{clip_summary}","Best Quote":"{best_quote}","Speakers":"{speaker_names}","Status":"Queued for Editing"}}]',
            },
          ],
          variables: [
            {
              name: "records[0].id",
              customName: "airtable_clips_id",
              type: "string",
            },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Assign clip editing to video editor",
        1200,
        'WEBINAR CLIPS READY FOR EDITING\\nWebinar: "{webinar_title}" ({duration_minutes} min)\\nSpeakers: {speaker_names} | Attendees: {attendee_count}\\n\\nClips to create ({clip_count}):\\n{clip_summary}\\n\\nBest quote: "{best_quote}"\\nAirtable: {airtable_clips_id}\\nRecording: {recording_url}',
      ),
      emailNode(
        "7",
        "Email: Send publish checklist to content ops",
        1440,
        "{content_ops_email}",
        "Webinar clips + transcript ready: {webinar_title}",
        'Hi Content Ops,\\n\\nThe webinar "{webinar_title}" recording has been processed.\\n\\nDetails:\\n- Date: {webinar_date}\\n- Duration: {duration_minutes} minutes\\n- Speakers: {speaker_names}\\n- Attendees: {attendee_count}\\n\\nClips identified ({clip_count}):\\n{clip_summary}\\n\\nBest shareable quote: "{best_quote}"\\n\\nPublish checklist:\\n1. Edit clips from timestamps above\\n2. Create thumbnail for each clip\\n3. Write social copy for distribution\\n4. Upload clips to YouTube Shorts / LinkedIn / X\\n5. Include transcript link in webinar recap blog post\\n\\nRecording: {recording_url}\\nAirtable: {airtable_clips_id}',
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log webinar processing",
        1680,
        "WebinarProcessing",
        '[["{webinar_title}","{webinar_date}","{duration_minutes}","{speaker_names}","{attendee_count}","{clip_count}","{best_quote}","{airtable_clips_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Webinar recording to clips: Zoom recording triggers AI transcription and content extraction, AI identifies 4 clip-worthy segments with timestamps, clip queue is created in Airtable, video editor is assigned via Slack, content ops receives a publish checklist email, and processing is logged in Sheets.",
  },

  // ── 97. Template Library Update Notifier | Content | Pattern D ──
  "Template Library Update Notifier": {
    nodes: [
      trigger("Google Drive: Template file updated webhook", "webhook_app", {
        source: "GoogleDrive",
        event: "file.modified",
        howItStarts:
          "Fires when a template file is updated in the shared Google Drive template library folder.",
        setup:
          "Connect Google Drive webhook for file modification events in the templates folder. Filter for document and spreadsheet file types.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Summarize template changes",
        240,
        "You are a template management assistant. Analyze the Google Drive file update event and summarize the changes. Return JSON with template_name, file_type, updated_by, change_summary (what was modified), previous_version, current_version, and impact_assessment (who is affected by this change).",
        "Google Drive template update event: {{{webhook_body}}}",
        [
          { field: "template_name", as: "template_name", type: "string" },
          { field: "file_type", as: "file_type", type: "string" },
          { field: "updated_by", as: "updated_by", type: "string" },
          { field: "change_summary", as: "change_summary", type: "string" },
          { field: "previous_version", as: "previous_version", type: "string" },
          { field: "current_version", as: "current_version", type: "string" },
          {
            field: "impact_assessment",
            as: "impact_assessment",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "runCode",
        "Identify affected teams and check for breaking changes",
        480,
        {
          runCodeDetails: {
            code: "const summary = (input.change_summary || '').toLowerCase(); const isBreaking = /removed|deleted|restructured|mandatory|required field|format change/.test(summary); const affectedTeams = []; if (/sales|proposal|quote/.test(input.template_name?.toLowerCase() || '')) affectedTeams.push('Sales'); if (/marketing|campaign|email/.test(input.template_name?.toLowerCase() || '')) affectedTeams.push('Marketing'); if (/hr|onboard|policy/.test(input.template_name?.toLowerCase() || '')) affectedTeams.push('HR'); if (affectedTeams.length === 0) affectedTeams.push('All Teams'); return { is_breaking_change: isBreaking, affected_teams: affectedTeams.join(', '), team_count: affectedTeams.length, requires_retraining: isBreaking };",
            outputVariables: [
              { name: "is_breaking_change", type: "boolean" },
              { name: "affected_teams", type: "string" },
              { name: "team_count", type: "number" },
              { name: "requires_retraining", type: "boolean" },
            ],
          },
        },
      ),
      n("4", "condition", "Is this a breaking change to the template?", 720, {
        conditionDetails: {
          conditions: [
            { field: "is_breaking_change", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Notify affected teams of template change",
        960,
        "{affected_team_emails}",
        "IMPORTANT: Template updated — {template_name} (v{current_version})",
        'Hi Team,\\n\\nThe template "{template_name}" has been updated from v{previous_version} to v{current_version}.\\n\\nUpdated by: {updated_by}\\nFile type: {file_type}\\n\\nWhat changed:\\n{change_summary}\\n\\nImpact assessment:\\n{impact_assessment}\\n\\nAffected teams: {affected_teams}\\nBreaking change: {is_breaking_change}\\nRetraining needed: {requires_retraining}\\n\\nPlease start using the updated version immediately. The previous version should no longer be used.\\n\\nIf you have questions about the changes, reach out to {updated_by}.',
      ),
      slackNode(
        "6",
        "Slack: Announce template update",
        1200,
        "TEMPLATE UPDATE: {template_name} (v{previous_version} -> v{current_version})\\nUpdated by: {updated_by}\\nChange: {change_summary}\\nBreaking change: {is_breaking_change}\\nAffected teams: {affected_teams}\\nImpact: {impact_assessment}",
      ),
      n(
        "7",
        "hitApi",
        "Airtable: Log version change in template registry",
        1440,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.airtable.com/v0/{airtable_base_id}/TemplateRegistry",
            headers: [
              { key: "Authorization", value: "Bearer {airtable_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "records",
                value:
                  '[{"fields":{"Template":"{template_name}","Previous Version":"{previous_version}","Current Version":"{current_version}","Updated By":"{updated_by}","Change Summary":"{change_summary}","Breaking Change":"{is_breaking_change}","Affected Teams":"{affected_teams}"}}]',
              },
            ],
            variables: [
              {
                name: "records[0].id",
                customName: "airtable_version_id",
                type: "string",
              },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log template update history",
        1680,
        "TemplateVersionHistory",
        '[["{template_name}","{previous_version}","{current_version}","{updated_by}","{change_summary}","{is_breaking_change}","{affected_teams}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Template library notifier: Google Drive file update triggers AI change summarization, affected teams are identified and breaking changes flagged, breaking changes trigger email notification to affected teams, Slack announces the update, Airtable logs the version change, and Sheets maintains the full update history.",
  },

  // ── 98. Social Listening Issue Escalation | Content | Pattern K ──
  "Social Listening Issue Escalation": {
    nodes: [
      trigger(
        "Webhook: Negative social mention spike detected",
        "webhook_app",
        {
          source: "SocialListening",
          event: "mention.spike_detected",
          howItStarts:
            "Fires when social listening tools detect a spike in negative brand mentions, indicating a potential PR issue.",
          setup:
            "Connect social listening platform webhook for negative mention spike alerts. Payload includes mention volume, sentiment breakdown, top posts, and affected platforms.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Classify issue severity and type",
        240,
        "You are a crisis communications analyst. Assess this negative mention spike and classify the issue. Return JSON with spike_volume, negative_percentage, top_complaint, affected_platforms (comma-separated), issue_category (product/service/PR/leadership/security), severity_level (low/medium/high/critical), is_crisis_risk (boolean), and recommended_response_time (e.g. '1 hour', '4 hours', '24 hours').",
        "Negative mention spike data: {{{webhook_body}}}",
        [
          { field: "spike_volume", as: "spike_volume", type: "number" },
          {
            field: "negative_percentage",
            as: "negative_percentage",
            type: "number",
          },
          { field: "top_complaint", as: "top_complaint", type: "string" },
          {
            field: "affected_platforms",
            as: "affected_platforms",
            type: "string",
          },
          { field: "issue_category", as: "issue_category", type: "string" },
          { field: "severity_level", as: "severity_level", type: "string" },
          { field: "is_crisis_risk", as: "is_crisis_risk", type: "boolean" },
          {
            field: "recommended_response_time",
            as: "recommended_response_time",
            type: "string",
          },
        ],
      ),
      n("3", "hitApi", "HubSpot: Match affected accounts and customers", 480, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"hs_social_last_engagement","operator":"HAS_PROPERTY"}]}]',
            },
          ],
          variables: [
            { name: "total", customName: "affected_contacts", type: "number" },
            {
              name: "results[0].properties.company",
              customName: "top_affected_company",
              type: "string",
            },
          ],
        },
      }),
      n("4", "runCode", "Determine escalation path based on severity", 720, {
        runCodeDetails: {
          code: "const severity = input.severity_level || 'low'; const isCrisis = input.is_crisis_risk; const escalationPath = isCrisis ? 'CEO + Head of PR + Legal' : severity === 'high' ? 'Head of PR + Social Team Lead' : severity === 'medium' ? 'Social Team Lead + Support Lead' : 'Social Team'; const channels = isCrisis ? '#war-room' : '#social-alerts'; const needsStatement = isCrisis || severity === 'high'; return { escalation_path: escalationPath, alert_channel: channels, needs_public_statement: needsStatement, response_sla: input.recommended_response_time };",
          outputVariables: [
            { name: "escalation_path", type: "string" },
            { name: "alert_channel", type: "string" },
            { name: "needs_public_statement", type: "boolean" },
            { name: "response_sla", type: "string" },
          ],
        },
      }),
      n("5", "condition", "Is this a PR crisis risk?", 960, {
        conditionDetails: {
          conditions: [
            { field: "is_crisis_risk", operator: "equals", value: "true" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Send urgent alert to escalation team",
        1200,
        'SOCIAL LISTENING ESCALATION ({severity_level} severity)\\nMention spike: {spike_volume} mentions ({negative_percentage}% negative)\\nTop complaint: "{top_complaint}"\\nPlatforms: {affected_platforms}\\nCategory: {issue_category}\\n\\nEscalation path: {escalation_path}\\nResponse SLA: {response_sla}\\nCrisis risk: {is_crisis_risk}\\nNeeds public statement: {needs_public_statement}\\nAffected known contacts: {affected_contacts}',
      ),
      emailNode(
        "7",
        "Email: Alert PR and support leadership",
        1440,
        "{pr_leadership_email}",
        "URGENT: Social mention spike — {severity_level} severity ({spike_volume} mentions)",
        "Social Listening Escalation Alert\\n\\nSeverity: {severity_level}\\nCrisis risk: {is_crisis_risk}\\n\\nDetails:\\n- Mention volume: {spike_volume}\\n- Negative sentiment: {negative_percentage}%\\n- Top complaint: {top_complaint}\\n- Platforms: {affected_platforms}\\n- Category: {issue_category}\\n\\nEscalation path: {escalation_path}\\nResponse SLA: {response_sla}\\nPublic statement needed: {needs_public_statement}\\n\\nAffected known customers: {affected_contacts}\\nTop affected company: {top_affected_company}\\n\\nPlease coordinate response immediately. Join {alert_channel} for real-time updates.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log social escalation incident",
        1680,
        "SocialEscalations",
        '[["{issue_category}","{severity_level}","{spike_volume}","{negative_percentage}","{top_complaint}","{affected_platforms}","{is_crisis_risk}","{escalation_path}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Social listening escalation: negative mention spike triggers AI severity classification, HubSpot matches affected customer accounts, escalation path is determined by severity, crisis-risk issues get urgent Slack alerts, PR and support leadership receive email alerts, and all escalation incidents are logged in Sheets.",
  },

  // ── 99. Blog QA / Preflight Checker | Content | Pattern I ──
  "Blog QA / Preflight Checker": {
    nodes: [
      trigger("WordPress: Blog post draft ready for QA", "webhook_app", {
        source: "WordPress",
        event: "post.draft_ready",
        howItStarts:
          "Fires when a blog post draft is marked as 'Ready for Review' in WordPress.",
        setup:
          "Connect WordPress webhook for post status changes to 'pending review'. Ensure post title, body, meta, and featured image are included.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Check grammar, spelling, and brand voice",
        240,
        "You are a content QA specialist. Review this blog draft for grammar, spelling, and brand voice compliance. Return JSON with post_title, post_url, word_count, grammar_issues (count of issues), spelling_errors (count), brand_voice_score (0-100), readability_grade (Flesch-Kincaid grade level), and grammar_notes (top 3 issues found, joined by |).",
        "WordPress draft post: {{{webhook_body}}}",
        [
          { field: "post_title", as: "post_title", type: "string" },
          { field: "post_url", as: "post_url", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
          { field: "grammar_issues", as: "grammar_issues", type: "number" },
          { field: "spelling_errors", as: "spelling_errors", type: "number" },
          {
            field: "brand_voice_score",
            as: "brand_voice_score",
            type: "number",
          },
          {
            field: "readability_grade",
            as: "readability_grade",
            type: "string",
          },
          { field: "grammar_notes", as: "grammar_notes", type: "string" },
        ],
      ),
      n("3", "runCode", "Validate SEO meta, slug, and structured data", 480, {
        runCodeDetails: {
          code: "const title = input.post_title || ''; const titleLen = title.length; const hasMetaDesc = true; const hasAltText = true; const slugOk = true; const seoScore = (titleLen >= 30 && titleLen <= 70 ? 25 : 0) + (hasMetaDesc ? 25 : 0) + (hasAltText ? 25 : 0) + (slugOk ? 25 : 0); const allChecksPassed = seoScore >= 75 && (input.grammar_issues || 0) <= 2 && (input.spelling_errors || 0) === 0 && (input.brand_voice_score || 0) >= 80; return { seo_score: seoScore, title_length: titleLen, all_checks_pass: allChecksPassed, qa_status: allChecksPassed ? 'PASS' : 'NEEDS FIXES' };",
          outputVariables: [
            { name: "seo_score", type: "number" },
            { name: "title_length", type: "number" },
            { name: "all_checks_pass", type: "boolean" },
            { name: "qa_status", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Do all QA checks pass?", 720, {
        conditionDetails: {
          conditions: [
            { field: "all_checks_pass", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Send QA report to editor with fixes needed",
        960,
        "{editor_email}",
        'Blog QA Report: {qa_status} — "{post_title}"',
        "Blog Post QA Report\\n\\nTitle: {post_title}\\nURL: {post_url}\\nWord count: {word_count}\\n\\nQA Status: {qa_status}\\n\\nGrammar & Spelling:\\n- Grammar issues: {grammar_issues}\\n- Spelling errors: {spelling_errors}\\n- Notes: {grammar_notes}\\n\\nBrand Voice: {brand_voice_score}/100\\nReadability: Grade {readability_grade}\\n\\nSEO:\\n- SEO score: {seo_score}/100\\n- Title length: {title_length} chars\\n\\nPlease address any issues before scheduling for publication.",
      ),
      slackNode(
        "6",
        "Slack: Post QA ready notification",
        1200,
        'BLOG QA {qa_status}: "{post_title}"\\nWords: {word_count} | Grammar issues: {grammar_issues} | Spelling: {spelling_errors}\\nBrand voice: {brand_voice_score}/100 | SEO: {seo_score}/100\\nReadability: Grade {readability_grade}\\n{post_url}',
      ),
      n(
        "7",
        "hitApi",
        "WordPress: Schedule post for publication if QA passes",
        1440,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{wp_domain}/wp-json/wp/v2/posts/{post_id}",
            headers: [
              { key: "Authorization", value: "Bearer {wp_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "status", value: "future" },
              { key: "date", value: "{scheduled_publish_date}" },
            ],
            variables: [
              { name: "id", customName: "scheduled_post_id", type: "number" },
              { name: "date", customName: "publish_date", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log blog QA results",
        1680,
        "BlogQALog",
        '[["{post_title}","{word_count}","{grammar_issues}","{spelling_errors}","{brand_voice_score}","{seo_score}","{qa_status}","{readability_grade}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "7", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "8"),
      e("7", "6"),
    ],
    summary:
      "Blog QA preflight: WordPress draft triggers AI grammar, spelling, and brand voice checks, SEO meta and slug are validated, passing posts are scheduled for publication in WordPress, failing posts get a detailed QA report emailed to the editor, Slack is notified of QA status, and all results are logged in Sheets.",
  },

  // ── 100. Content Gap Reporter from Support Tickets | Content | Pattern L ──
  "Content Gap Reporter from Support Tickets": {
    nodes: [
      trigger(
        "Schedule: Weekly support ticket content gap analysis",
        "schedule",
        {
          schedule: "Every Friday at 2:00 PM",
          howItStarts:
            "Runs weekly to analyze support tickets and identify recurring topics that indicate missing or inadequate knowledge base content.",
          setup:
            "Configure weekly schedule for Friday 2PM. Ensure Zendesk API credentials are configured with read access to ticket data.",
          inputFields: [],
        },
      ),
      n("2", "hitApi", "Zendesk API: Fetch common support ticket topics", 240, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_domain}.zendesk.com/api/v2/search.json?query=type:ticket+created>{seven_days_ago}&sort_by=created_at&sort_order=desc&per_page=100",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "count", customName: "ticket_count", type: "number" },
            { name: "results", customName: "tickets_raw", type: "string" },
          ],
        },
      }),
      aiNode(
        "3",
        "OpenAI: Cluster tickets into theme groups",
        480,
        "You are a support data analyst. Cluster these support tickets into recurring theme groups. Return JSON with total_tickets_analyzed, theme_groups (top 5 themes with counts, joined by |||, format: 'Theme Name (count)'), top_theme_name, top_theme_count, top_theme_sample_question, repeat_question_percentage (what % of tickets are repeat questions), and documentation_gaps (specific articles/guides missing, joined by |).",
        "Support tickets from the past week: {tickets_raw}. Total: {ticket_count}.",
        [
          {
            field: "total_tickets_analyzed",
            as: "total_tickets_analyzed",
            type: "number",
          },
          { field: "theme_groups", as: "theme_groups", type: "string" },
          { field: "top_theme_name", as: "top_theme_name", type: "string" },
          { field: "top_theme_count", as: "top_theme_count", type: "number" },
          {
            field: "top_theme_sample_question",
            as: "top_theme_sample_question",
            type: "string",
          },
          {
            field: "repeat_question_percentage",
            as: "repeat_question_percentage",
            type: "number",
          },
          {
            field: "documentation_gaps",
            as: "documentation_gaps",
            type: "string",
          },
        ],
      ),
      n("4", "runCode", "Match themes against existing KB articles", 720, {
        runCodeDetails: {
          code: "const gaps = (input.documentation_gaps || '').split('|').filter(g => g.trim()); const themes = (input.theme_groups || '').split('|||').filter(t => t.trim()); const gapIdentified = gaps.length > 0; const highImpact = (input.repeat_question_percentage || 0) > 25; const priority = highImpact ? 'High — deflect ' + input.repeat_question_percentage + '% of repeat tickets' : 'Medium — improve self-service'; return { gap_identified: gapIdentified, gap_count: gaps.length, theme_count: themes.length, is_high_impact: highImpact, content_priority: priority };",
          outputVariables: [
            { name: "gap_identified", type: "boolean" },
            { name: "gap_count", type: "number" },
            { name: "theme_count", type: "number" },
            { name: "is_high_impact", type: "boolean" },
            { name: "content_priority", type: "string" },
          ],
        },
      }),
      n(
        "5",
        "condition",
        "Are there content gaps worth creating content for?",
        960,
        {
          conditionDetails: {
            conditions: [
              { field: "gap_identified", operator: "equals", value: "true" },
            ],
          },
        },
      ),
      n(
        "6",
        "hitApi",
        "Asana: Create content tasks for identified gaps",
        1200,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://app.asana.com/api/1.0/tasks",
            headers: [
              { key: "Authorization", value: "Bearer {asana_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "data",
                value:
                  '{"name":"KB Gap: {top_theme_name} ({top_theme_count} tickets/week)","projects":["{asana_kb_project_id}"],"notes":"Content Gap from Support Analysis\\n\\nTop theme: {top_theme_name} ({top_theme_count} tickets this week)\\nSample question: {top_theme_sample_question}\\nRepeat ticket %: {repeat_question_percentage}%\\n\\nAll themes: {theme_groups}\\n\\nDocumentation gaps to fill:\\n{documentation_gaps}\\n\\nPriority: {content_priority}"}',
              },
            ],
            variables: [
              {
                name: "data.gid",
                customName: "asana_gap_task_id",
                type: "string",
              },
              {
                name: "data.permalink_url",
                customName: "asana_task_url",
                type: "string",
              },
            ],
          },
        },
      ),
      slackNode(
        "7",
        "Slack: Alert content team of KB gaps",
        1440,
        'WEEKLY CONTENT GAP REPORT (from {total_tickets_analyzed} support tickets)\\nRepeat question rate: {repeat_question_percentage}%\\n\\nTop theme: {top_theme_name} ({top_theme_count} tickets)\\nSample: "{top_theme_sample_question}"\\n\\nAll themes:\\n{theme_groups}\\n\\nGaps identified: {gap_count}\\nDocs to create: {documentation_gaps}\\nPriority: {content_priority}\\nAsana: {asana_task_url}',
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log weekly content gap analysis",
        1680,
        "ContentGapReports",
        '[["{total_tickets_analyzed}","{repeat_question_percentage}","{top_theme_name}","{top_theme_count}","{gap_count}","{documentation_gaps}","{content_priority}","{asana_gap_task_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Support-to-content gap reporter: weekly schedule fetches Zendesk tickets, AI clusters them into theme groups, themes are matched against existing KB articles, identified gaps create Asana content tasks, content team is alerted via Slack with the gap report, and weekly analysis is logged in Sheets.",
  },
};
